import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import createAndDownloadBackup from '../../utils/backupDownload';
import useDashboardData from './useDashboardData';
import VendorWorkflow from './VendorWorkflow';
import CustomerWorkflow from './CustomerWorkflow';
import EmployeeWorkflow from './EmployeeWorkflow';
import FinancialOverview from './FinancialOverview';
import CompanyPanel from './CompanyPanel';
import BankingPanel from './BankingPanel';
import AccountBalancePanel from './AccountBalancePanel';
import QuickActionsPanel from './QuickActionsPanel';
import BackupStatusPanel from './BackupStatusPanel';
import CashFlowMiniChart from './CashFlowMiniChart';
import DateRangePicker from './DateRangePicker';

/**
 * Home screen of the desktop application: a three-column structure with
 * the accounting workflow map (Vendors / Customers / Employees) on the
 * left, Company & Banking shortcut panels in the middle, and a gray
 * status sidebar (balances, quick actions, backup) on the far right.
 */
export default function WorkflowDashboard() {
  const { user } = useAuth();
  const now = new Date();
  const [dateRange, setDateRange] = useState({
    startDate: `${now.getFullYear()}-01-01`,
    endDate: now.toISOString().split('T')[0],
  });
  const { kpi, balances, backups, recentTransactions, salesTrend, expenseTrend, topCustomers, topProducts, loading, runBackup } = useDashboardData(dateRange);
  const [backingUp, setBackingUp] = useState(false);

  const handleBackup = async () => {
    setBackingUp(true);
    try {
      await createAndDownloadBackup('Manual');
      toast.success('Backup created and downloaded');
      await runBackup();
    } catch (err) {
      toast.error(err.message || 'Backup failed');
    } finally {
      setBackingUp(false);
    }
  };

  if (loading && !kpi) {
    return (
      <div className="desk-body">
        <div className="desk-main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="desk-body">
      <div className="desk-main">
        <VendorWorkflow />
        <CustomerWorkflow />
        <EmployeeWorkflow />
        <div className="desk-main-header">
          <FinancialOverview kpi={kpi} balances={balances} salesTrend={salesTrend} expenseTrend={expenseTrend} topCustomers={topCustomers} topProducts={topProducts} recentTransactions={recentTransactions} />
          <DateRangePicker value={dateRange} onChange={setDateRange} />
        </div>
      </div>

      <div className="desk-right">
        <CompanyPanel />
        <BankingPanel />
      </div>

      <aside className="desk-statusbar">
        <AccountBalancePanel kpi={kpi} balances={balances} />
        <CashFlowMiniChart salesTrend={salesTrend} expenseTrend={expenseTrend} />
        <QuickActionsPanel />
        <BackupStatusPanel backups={backups} onRefresh={runBackup} busy={backingUp} setBusy={setBackingUp} />
        <div className="desk-status-footer">
          <div className="desk-status-user">{user?.first_name} {user?.last_name || ''}</div>
          <div className="desk-status-role">{user?.role_name || 'User'} · LedgerPro</div>
        </div>
      </aside>
    </div>
  );
}
